<template>
  <div class="shopee-report-page">
    <KTCodePreview :title="'Báo cáo — Đơn hàng theo sàn'">
      <template #preview>
        <b-card class="mb-3 shopee-report-filter-card">
          <h6 class="text-muted mb-3">Bộ lọc</h6>
          <b-row align-v="start" class="shopee-report-filter-row mb-0">
            <b-col cols="12" md="3">
              <b-form-group label="Từ ngày" label-for="or-df">
                <b-form-input id="or-df" v-model="dateFrom" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group label="Đến ngày" label-for="or-dt">
                <b-form-input id="or-dt" v-model="dateTo" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" class="shopee-report-select-conn-col">
              <b-form-group label="Gian hàng" label-for="or-conn">
                <b-form-select
                  id="or-conn"
                  v-model="selectedConnectionId"
                  class="select-style"
                  :options="shopOptions"
                  size="sm"
                  :disabled="loadingConnections"
                >
                  <template #first>
                    <b-form-select-option :value="null">
                      {{ connections.length === 0 ? '(Chưa có kết nối)' : 'Tất cả gian hàng' }}
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="shopee-report-filter-actions">
            <b-button variant="primary" size="sm" :disabled="!canSubmit" @click="reload">
              <b-spinner v-if="loading" small class="mr-1"></b-spinner>
              Tải dữ liệu
            </b-button>
          </div>
        </b-card>

        <b-alert v-if="metaNote" variant="secondary" show class="small">{{ metaNote }}</b-alert>
        <b-alert v-if="error" variant="danger" show>{{ error }}</b-alert>

        <b-card v-if="totals && fetched" class="mb-3" body-class="py-3">
          <h6 class="text-muted mb-3">Tổng kết kỳ</h6>
          <b-row>
            <b-col cols="6" md="4" class="mb-2">
              <div class="text-muted small">Đơn giao thành công</div>
              <div class="h5 mb-0">{{ fmtInt(totals.deliveredSuccessCount) }}</div>
            </b-col>
            <b-col cols="6" md="4" class="mb-2">
              <div class="text-muted small">Chuyển hoàn</div>
              <div class="h5 mb-0 text-danger">{{ fmtInt(totals.deliveredReturnCount) }}</div>
            </b-col>
            <b-col cols="12" md="4" class="mb-2">
              <div class="text-muted small">SL sản phẩm bán</div>
              <div class="h5 mb-0">{{ fmtInt(totals.quantitySold) }}</div>
            </b-col>
          </b-row>
        </b-card>

        <h6 v-if="byPlatform.length" class="mt-3 mb-2">Theo sàn / gian hàng</h6>
        <b-table
          v-if="byPlatform.length || loading"
          striped
          hover
          responsive
          class="mb-4"
          :busy="loading"
          :fields="platformFields"
          :items="byPlatform"
          show-empty
          empty-text="Không có dữ liệu"
        >
          <template #cell(deliveredSuccessCount)="d">{{ fmtInt(d.value) }}</template>
          <template #cell(deliveredReturnCount)="d">{{ fmtInt(d.value) }}</template>
        </b-table>

        <h6 v-if="byProduct.length || loading" class="mb-2">Sản phẩm bán ra</h6>
        <b-table
          v-if="byProduct.length || loading"
          striped
          hover
          responsive
          :busy="loading"
          :fields="productFields"
          :items="byProduct"
          show-empty
          empty-text="Không có dữ liệu"
        >
          <template #cell(quantitySold)="d">{{ fmtInt(d.value) }}</template>
        </b-table>

        <div v-if="productTotalRows > productLimit" class="d-flex justify-content-end mt-2">
          <b-pagination
            v-model="productPage"
            :total-rows="productTotalRows"
            :per-page="productLimit"
            align="center"
            class="custom-pagination mb-0"
            @input="fetchReport"
          />
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
  import KTCodePreview from '@/view/content/CodePreview.vue'
  import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
  import { RepositoryFactory } from '@/repositories/RepositoryFactory'
  import { isoDateMinusDays, isoToday } from './formatters'

  const shopeeAdminRepo = RepositoryFactory.get('shopeeAdmin')
  const platformRepo = RepositoryFactory.get('platformIntegration')

  export default {
    name: 'ShopeeReportOrderReport',
    components: { KTCodePreview },
    data() {
      return {
        dateFrom: '',
        dateTo: '',
        productPage: 1,
        productLimit: 50,
        loading: false,
        loadingConnections: false,
        connections: [],
        selectedConnectionId: null,
        error: null,
        meta: null,
        byPlatform: [],
        byProduct: [],
        totals: null,
        productPagination: null,
        fetched: false,
        platformFields: [
          { key: 'platform', label: 'Sàn' },
          { key: 'shopName', label: 'Tên shop' },
          {
            key: 'deliveredSuccessCount',
            label: 'Giao thành công',
            tdClass: 'text-right',
            thClass: 'text-right',
          },
          {
            key: 'deliveredReturnCount',
            label: 'Chuyển hoàn',
            tdClass: 'text-right',
            thClass: 'text-right',
          },
        ],
        productFields: [
          { key: 'platform', label: 'Sàn' },
          { key: 'shopName', label: 'Tên shop' },
          { key: 'productCode', label: 'Mã SP' },
          { key: 'productName', label: 'Tên SP' },
          {
            key: 'quantitySold',
            label: 'SL bán',
            tdClass: 'text-right',
            thClass: 'text-right',
          },
        ],
      }
    },
    computed: {
      shopOptions() {
        return this.connections.map((c) => ({
          value: c.id,
          text: c.shopName || `Shop #${c.id}`,
        }))
      },
      canSubmit() {
        return !!(this.dateFrom && this.dateTo && !this.loading)
      },
      metaNote() {
        if (!this.meta) return ''
        if (this.meta.dateFrom && this.meta.dateTo) {
          return `${this.meta.dateFrom} → ${this.meta.dateTo}`
        }
        return ''
      },
      productTotalRows() {
        const n = Number(this.productPagination?.total)
        return Number.isNaN(n) ? 0 : n
      },
    },
    async created() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán', route: '/platform-integration/orders' },
        { title: 'Báo cáo', route: '/platform-integration/reports/order-report' },
        { title: 'Báo cáo đơn hàng' },
      ])
      this.dateTo = isoToday()
      this.dateFrom = isoDateMinusDays(29)
      await this.fetchConnections()
      await this.fetchReport()
    },
    methods: {
      async fetchConnections() {
        try {
          this.loadingConnections = true
          const res = await platformRepo.getConfigs({ platform: 'shopee' })
          this.connections = (res.items || []).filter((c) => c.status === 'connected')
          if (this.connections.length === 1 && this.selectedConnectionId == null) {
            this.selectedConnectionId = this.connections[0].id
          }
        } catch (e) {
          console.error(e)
        } finally {
          this.loadingConnections = false
        }
      },
      fmtInt(v) {
        if (v == null || v === '') return '—'
        const n = Number(v)
        if (Number.isNaN(n)) return String(v)
        return new Intl.NumberFormat('vi-VN').format(n)
      },
      reload() {
        this.productPage = 1
        this.fetchReport()
      },
      async fetchReport() {
        this.error = null
        if (!this.dateFrom || !this.dateTo) {
          this.error = 'Vui lòng chọn đủ từ ngày và đến ngày.'
          return
        }
        try {
          this.loading = true
          this.fetched = true
          const params = {
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            productPage: this.productPage,
            productLimit: this.productLimit,
          }
          if (this.selectedConnectionId != null) {
            params.connectionId = this.selectedConnectionId
          }
          const raw = await shopeeAdminRepo.getReportOrderReport(params)
          this.meta = raw?.meta ?? null
          this.byPlatform = Array.isArray(raw?.byPlatform) ? raw.byPlatform : []
          this.byProduct = Array.isArray(raw?.byProduct) ? raw.byProduct : []
          this.totals = raw?.totals ?? null
          this.productPagination = raw?.productPagination ?? null
        } catch (e) {
          console.error(e)
          this.byPlatform = []
          this.byProduct = []
          this.totals = null
          this.error =
            e.response?.data?.error || e.response?.data?.message || 'Không tải được báo cáo.'
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import './shopee-report-filters.scss';
</style>
